import type { BondsDetailsIssuerFactDocumentData, BondsDetailsIssuerFactValue, BondsDetailsIssuerFactsData } from '@/proto/bonds'
import type {
    BondV2CreditRatingIntelligenceData,
    BondV2FinancialTrendConfig,
    BondV2FinancialTrendData,
    BondV2IssuerGlanceMetric,
    BondV2PeerMetricData,
    BondV2RatingDriverItem,
    BondV2RatingTimelineItem,
    BondV2StrengthRiskGroup,
    BondV2StrengthRiskItem,
    BondV2StrengthsRisksData
} from './bondV2Data'

export interface BondV2RegulatoryDocument {
    label: string
    fileUrl: string
    fileName: string
    documentId: string
}

export interface BondV2RegulatoryAssuranceData {
    title: string
    documents: BondV2RegulatoryDocument[]
}

export interface BondV2BasicDetailsData {
    hasModule: boolean
    title?: string
    aboutIssuer?: {
        label: string
        value: string
    }
    debentureTrustee?: {
        label: string
        value: string
    }
    issuanceDate?: {
        label: string
        value: string
    }
    modeOfIssuance?: {
        label: string
        value: string
    }
    ownership?: {
        label: string
        value: string
    }
    securityType?: {
        label: string
        value: string
    }
    whyThisBond?: {
        label: string
        items: string[]
    }
    extraRows: Array<{
        fieldKey: string
        label: string
        value: string
    }>
}

const DOCUMENTS_MODULE_KEY = 'DOCUMENTS'
const BASIC_DETAILS_MODULE_KEY = 'BASIC_DETAILS'
const STRENGTHS_RISK_MODULE_KEY = 'STRENGTHS_RISK'
const CREDIT_RATING_INTELLIGENCE_MODULE_KEY = 'CREDIT_RATING_INTELLIGENCE'
const SECTOR_SUPPORT_DATA_MODULE_KEY = 'SECTOR_SUPPORT_DATA'
const FINANCIAL_DATA_MODULE_KEY = 'FINANCIAL_DATA'
const ISSUER_METRICS_MODULE_KEY = 'ISSUER_MATRICS'
const BASIC_DETAILS_STANDARD_FIELD_KEYS = new Set([
    'ABOUT_THE_ISSUER',
    'DEBENTURE_TRUSTEE',
    'ISSUANCE_DATE',
    'MODE_OF_ISSUANCE',
    'OWNERSHIP',
    'SECURITY_TYPE',
    'WHY_THIS_BOND'
])

const FINANCIAL_DISTRIBUTION_COLORS = [
    '#4f67e8',
    '#6b7d00',
    '#a31616',
    '#9a3412',
    '#8f2a3a',
    '#b45309',
    '#0027af',
    '#0f766e'
]

const parseDocumentValueJson = (valueJson?: string | null): BondsDetailsIssuerFactDocumentData | null => {
    if (!valueJson) return null

    try {
        return JSON.parse(valueJson) as BondsDetailsIssuerFactDocumentData
    } catch {
        return null
    }
}

type IssuerFactPayload = Record<string, unknown>

const parseFactPayload = (valueJson?: string | null): IssuerFactPayload => {
    if (!valueJson) return {}

    try {
        const parsed = JSON.parse(valueJson)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : { value: parsed }
    } catch {
        return {}
    }
}

const factFieldConfig = (fact: BondsDetailsIssuerFactValue) => {
    const payload = parseFactPayload(fact.valueJson)
    const fieldConfig = payload.fieldConfig

    return fieldConfig && typeof fieldConfig === 'object' && !Array.isArray(fieldConfig)
        ? fieldConfig as Record<string, unknown>
        : {}
}

const isUsableFact = (fact: BondsDetailsIssuerFactValue) => {
    const fieldConfig = factFieldConfig(fact)

    return fact.isEnabled
        && fact.isInvestorVisible
        && fieldConfig.isEnabled !== false
        && fieldConfig.isInvestorVisible !== false
}

const issuerMetricFactOrder = (fact: BondsDetailsIssuerFactValue) => {
    const order = Number(factFieldConfig(fact).displayOrder)

    return Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER
}

const usableFacts = (issuerFactsData: BondsDetailsIssuerFactsData | undefined, moduleKey: string) => {
    return issuerFactsData?.modules
        .find((module) => module.moduleKey === moduleKey)
        ?.facts
        .filter(isUsableFact) ?? []
}

const textValue = (fact?: BondsDetailsIssuerFactValue) => {
    if (!fact) return ''

    const payload = parseFactPayload(fact.valueJson)
    const value = fact.displayValue ?? payload.value ?? payload.text ?? payload.label

    return value == null ? '' : String(value).trim()
}

const formatIndianNumber = (value: number, maximumFractionDigits = 2) => {
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits
    })
}

const formatCurrencyFactValue = (value: number) => `₹${formatIndianNumber(value)} Cr`

const formattedScalarFactValue = (fact: BondsDetailsIssuerFactValue) => {
    const rawValue = textValue(fact)
    if (!rawValue) return ''
    if (fact.fieldType !== 'NUMBER') return rawValue

    const numericValue = Number(rawValue.replace(/,/g, ''))
    if (!Number.isFinite(numericValue)) return rawValue

    const unitType = fact.unitType?.toUpperCase()
    if (unitType === 'CURRENCY') return formatCurrencyFactValue(numericValue)
    if (unitType === 'PERCENTAGE') return rawValue.includes('%') ? rawValue : `${formatIndianNumber(numericValue)}%`
    if (unitType === 'RATIO' || unitType === 'TIMES') return rawValue.toLowerCase().includes('x') ? rawValue : `${formatIndianNumber(numericValue)}x`

    return formatIndianNumber(numericValue)
}

const factSchemaIcon = (fact: BondsDetailsIssuerFactValue) => {
    const payload = parseFactPayload(fact.fieldSchemaJson)
    const icon = payload.icon
    if (!icon || typeof icon !== 'object' || Array.isArray(icon)) return ''

    const iconRecord = icon as Record<string, unknown>
    return iconRecord.fileUrl == null ? '' : String(iconRecord.fileUrl).trim()
}

const factDescription = (fact: BondsDetailsIssuerFactValue) => {
    const description = (fact as BondsDetailsIssuerFactValue & { description?: string }).description

    return description == null ? '' : description.trim()
}

const listItems = (fact?: BondsDetailsIssuerFactValue) => {
    if (!fact) return []

    const payload = parseFactPayload(fact.valueJson)
    const payloadItems = Array.isArray(payload.items) ? payload.items : []
    const protoItems = fact.items.map((item) => {
        if (item.displayValue) return item.displayValue

        try {
            const parsed = JSON.parse(item.valueJson)
            return typeof parsed === 'string' ? parsed : parsed?.value
        } catch {
            return ''
        }
    })

    const items = [...protoItems, ...payloadItems]
        .map((item) => {
            if (typeof item === 'string') return item
            if (!item || typeof item !== 'object' || Array.isArray(item)) return ''

            return item.value ?? item.text ?? item.label ?? ''
        })
        .map((item) => String(item).trim())
        .filter(Boolean)

    return Array.from(new Set(items))
}

const objectListItems = (fact?: BondsDetailsIssuerFactValue): Array<Record<string, unknown> & { displayOrder?: number }> => {
    if (!fact) return []

    const payload = parseFactPayload(fact.valueJson)
    const payloadItems = Array.isArray(payload.items) ? payload.items : []
    const protoItems = fact.items
        .map((item) => {
            try {
                const parsed = JSON.parse(item.valueJson)
                return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                    ? { ...(parsed as Record<string, unknown>), displayOrder: item.displayOrder }
                    : null
            } catch {
                return null
            }
        })
        .filter(Boolean) as Array<Record<string, unknown> & { displayOrder?: number }>

    const items = protoItems.length
        ? protoItems
        : payloadItems
            .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
            .map((item, index) => ({ ...(item as Record<string, unknown>), displayOrder: index }))

    return items.sort((first, second) => (first.displayOrder ?? 0) - (second.displayOrder ?? 0))
}

const objectText = (item: Record<string, unknown>, key: string) => {
    const value = item[key]
    return value == null ? '' : String(value).trim()
}

const objectNumber = (item: Record<string, unknown>, key: string) => {
    const value = item[key]
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : null
}

const objectIcon = (item: Record<string, unknown>) => {
    const icon = item.icon
    if (!icon || typeof icon !== 'object' || Array.isArray(icon)) return {}

    const iconRecord = icon as Record<string, unknown>
    const fileUrl = iconRecord.fileUrl == null ? '' : String(iconRecord.fileUrl).trim()
    if (!fileUrl) return {}

    return {
        iconUrl: fileUrl,
        iconLabel: String(iconRecord.title || iconRecord.fileName || '').trim()
    }
}

const strengthRiskItems = (fact?: BondsDetailsIssuerFactValue): BondV2StrengthRiskItem[] => {
    return objectListItems(fact)
        .map((item) => ({
            title: objectText(item, 'HEADING'),
            description: objectText(item, 'DESCRIPTION'),
            ...objectIcon(item)
        }))
        .filter((item) => item.title && item.description)
        .slice(0, 6)
}

const ratingScore = (rating: string, index: number) => {
    const normalizedRating = rating.toUpperCase().replace(/\s+/g, '')
    const scoreMap: Record<string, number> = {
        AAA: 94,
        'AA+': 90,
        AA: 86,
        'AA-': 82,
        'A+': 78,
        A: 74,
        'A-': 70,
        'BBB+': 66,
        BBB: 62,
        'BBB-': 58,
        'BB+': 54,
        BB: 50,
        'BB-': 46,
        'B+': 42,
        B: 38,
        'B-': 34,
        C: 28,
        D: 22
    }

    return scoreMap[normalizedRating] ?? 42 + (index * 8)
}

const ratingValueOptions = [
    { title: 'AAA', value: 94 },
    { title: 'AA+', value: 90 },
    { title: 'AA', value: 86 },
    { title: 'AA-', value: 82 },
    { title: 'A+', value: 78 },
    { title: 'A', value: 74 },
    { title: 'A-', value: 70 },
    { title: 'BBB+', value: 66 },
    { title: 'BBB', value: 62 },
    { title: 'BBB-', value: 58 },
    { title: 'BB+', value: 54 },
    { title: 'BB', value: 50 },
    { title: 'BB-', value: 46 },
    { title: 'B+', value: 42 },
    { title: 'B', value: 38 },
    { title: 'B-', value: 34 },
    { title: 'C', value: 28 },
    { title: 'D', value: 22 }
]

const ratingLabelFromValue = (value?: number) => {
    return ratingValueOptions.find((item) => item.value === value)?.title
}

const ratingTimelineItems = (fact?: BondsDetailsIssuerFactValue): BondV2RatingTimelineItem[] => {
    const tones: BondV2RatingTimelineItem['tone'][] = ['orange', 'blue', 'green', 'indigo']

    return objectListItems(fact)
        .map((item, index) => {
            const year = objectNumber(item, 'YEAR')
            const rating = objectText(item, 'RATING')

            return {
                year: year == null ? objectText(item, 'YEAR') : String(year),
                title: objectText(item, 'TITLE'),
                rating,
                description: objectText(item, 'DESCRIPTION'),
                value: ratingScore(rating, index),
                tone: tones[index] ?? 'indigo'
            }
        })
        .filter((item) => item.year && item.title && item.rating && item.description)
        .slice(0, 4)
}

const ratingDriverTone = (status: string): BondV2RatingDriverItem['tone'] => {
    const normalizedStatus = status.toLowerCase()

    if (normalizedStatus.includes('positive') || normalizedStatus.includes('support')) return 'green'
    if (normalizedStatus.includes('neutral')) return 'amber'
    if (normalizedStatus.includes('monitor') || normalizedStatus.includes('watch')) return 'red'
    if (normalizedStatus.includes('negative') || normalizedStatus.includes('risk')) return 'red'

    return 'blue'
}

const ratingDriverItems = (fact?: BondsDetailsIssuerFactValue): BondV2RatingDriverItem[] => {
    return objectListItems(fact)
        .map((item) => {
            const status = objectText(item, 'LABEL')

            return {
                title: objectText(item, 'HEADING'),
                status,
                tone: ratingDriverTone(status),
                description: objectText(item, 'DESCRIPTION')
            }
        })
        .filter((item) => item.title && item.status && item.description)
        .slice(0, 6)
}

const metricConfigNumber = (metricConfig: Record<string, unknown>, key: string) => {
    const value = metricConfig[key]
    const numericValue = Number(value)

    return Number.isFinite(numericValue) ? numericValue : null
}

const metricPosition = (value: number, min: number, max: number) => {
    if (max === min) return 50

    return Math.max(1, Math.min(99, ((value - min) / (max - min)) * 100))
}

const formatPeerMetricValue = (value: number, unitType?: string) => {
    if (unitType === 'RATING') return ratingLabelFromValue(value) || String(value)
    if (unitType === 'PERCENTAGE') return `${value}%`
    if (unitType === 'RATIO' || unitType === 'TIMES') return `${value}x`
    if (unitType === 'CURRENCY') return `₹${value.toLocaleString('en-IN')} Cr`

    return value.toLocaleString('en-IN')
}

const inferredHigherBetter = (metricKey: string) => {
    const lowerBetterMetricKeys = new Set(['GNPA', 'NNPA', 'LEVERAGE', 'DEBT_EQUITY', 'DEBT_TO_EQUITY'])

    return !lowerBetterMetricKeys.has(metricKey)
}

const sectorMetricOrder = (value: Record<string, unknown>, fallbackIndex: number) => {
    const valueOrder = Number(value.displayOrder ?? value.order ?? value.display_order)
    if (Number.isFinite(valueOrder)) return valueOrder

    const metricConfig = value.metricConfig
    if (!metricConfig || typeof metricConfig !== 'object' || Array.isArray(metricConfig)) return fallbackIndex

    const metricConfigRecord = metricConfig as Record<string, unknown>
    const configOrder = Number(metricConfigRecord.displayOrder ?? metricConfigRecord.order ?? metricConfigRecord.display_order)
    return Number.isFinite(configOrder) ? configOrder : fallbackIndex
}

const mapSectorComparisonMetrics = (issuerFactsData?: BondsDetailsIssuerFactsData): BondV2PeerMetricData[] => {
    return (issuerFactsData?.sectorComparison?.values ?? [])
        .map((value, index) => ({ value, index }))
        .filter(({ value }) => value.isInvestorVisible !== false && value.metricConfig?.isActive !== false)
        .sort((first, second) => {
            return sectorMetricOrder(first.value as unknown as Record<string, unknown>, first.index)
                - sectorMetricOrder(second.value as unknown as Record<string, unknown>, second.index)
        })
        .map(({ value }) => {
            const metricConfig = (value.metricConfig ?? {}) as Record<string, unknown>
            const issuerValue = value.issuerValue
            const sectorMinValue = metricConfigNumber(metricConfig, 'sectorMinValue')
            const sectorAverageValue = metricConfigNumber(metricConfig, 'sectorAverageValue')
            const sectorMaxValue = metricConfigNumber(metricConfig, 'sectorMaxValue')

            if (
                issuerValue == null
                || sectorMinValue == null
                || sectorAverageValue == null
                || sectorMaxValue == null
            ) {
                return null
            }

            const unitType = metricConfig.unitType == null ? undefined : String(metricConfig.unitType)
            const metricKey = value.metricKey || String(metricConfig.metricKey || '')
            const isHigherBetter = typeof metricConfig.isHigherBetter === 'boolean'
                ? metricConfig.isHigherBetter
                : inferredHigherBetter(metricKey)
            const topValue = isHigherBetter ? sectorMaxValue : sectorMinValue

            return {
                label: value.metricLabel || String(metricConfig.metricLabel || value.metricKey),
                value: formatPeerMetricValue(issuerValue, unitType),
                issuerValue: formatPeerMetricValue(issuerValue, unitType),
                medianValue: formatPeerMetricValue(sectorAverageValue, unitType),
                topValue: formatPeerMetricValue(topValue, unitType),
                helper: isHigherBetter ? 'Higher is better' : 'Lower is better',
                issuer: metricPosition(issuerValue, sectorMinValue, sectorMaxValue),
                median: metricPosition(sectorAverageValue, sectorMinValue, sectorMaxValue),
                top: metricPosition(topValue, sectorMinValue, sectorMaxValue)
            }
        })
        .filter(Boolean) as BondV2PeerMetricData[]
}

const mapIssuerFactSectorSupportData = (issuerFactsData?: BondsDetailsIssuerFactsData) => {
    const module = issuerFactsData?.modules.find((item) => item.moduleKey === SECTOR_SUPPORT_DATA_MODULE_KEY)
    const metrics = mapSectorComparisonMetrics(issuerFactsData)
    if (!module?.title) return undefined

    const facts = usableFacts(issuerFactsData, SECTOR_SUPPORT_DATA_MODULE_KEY)
    const factByKey = new Map(facts.map((fact) => [fact.fieldKey, fact]))
    const tags = listItems(factByKey.get('PEER_COMPARISON_TAGS')).slice(0, 4)
    const summaryFact = factByKey.get('WHERE_IT_STANDS_OUT')
    const summary = textValue(summaryFact)
    const subtext = textValue(factByKey.get('WHERE_IT_STANDS_OUT_SUBTEXT'))

    if (!module.description && !tags.length && !summary && !subtext && !metrics.length) return undefined

    return {
        title: module.title,
        description: module?.description,
        summaryLabel: summaryFact?.label,
        summary,
        tags,
        subtext,
        metrics
    }
}

type FinancialMetricSchema = {
    key: string
    label: string
    graphType: string
    unitType?: string
}

type FinancialDistributionChart = Extract<
    BondV2FinancialTrendConfig,
    { type: 'doughnut-grid' }
>['charts'][number]

const parseJsonValue = (value?: string | null): unknown => {
    if (value == null || value === '') return undefined

    try {
        return JSON.parse(value)
    } catch {
        return value
    }
}

const parseFinancialSchemaMetrics = (fieldSchemaJson?: string | null): FinancialMetricSchema[] => {
    const parsedSchema = parseFactPayload(fieldSchemaJson)
    const metrics = Array.isArray(parsedSchema.metrics) ? parsedSchema.metrics : []

    return metrics
        .map((metric) => {
            if (!metric || typeof metric !== 'object' || Array.isArray(metric)) return null

            const metricRecord = metric as Record<string, unknown>
            const key = metricRecord.key == null ? '' : String(metricRecord.key).trim()
            if (!key) return null

            return {
                key,
                label: metricRecord.label == null ? key : String(metricRecord.label).trim(),
                graphType: metricRecord.graphType == null ? 'LINE' : String(metricRecord.graphType).trim().toUpperCase(),
                unitType: metricRecord.unitType == null ? undefined : String(metricRecord.unitType).trim().toUpperCase()
            }
        })
        .filter(Boolean) as FinancialMetricSchema[]
}

const financialMetricLabel = (metric: FinancialMetricSchema) => {
    if (['AUM', 'PAT'].includes(metric.key)) return metric.key

    return metric.label || metric.key
}

const financialPeriodOrder = (period: string, fallbackOrder: number) => {
    const match = period.match(/(\d{2,4})/)
    if (!match) return fallbackOrder

    const numericYear = Number(match[1])
    if (!Number.isFinite(numericYear)) return fallbackOrder

    return numericYear < 100 ? 2000 + numericYear : numericYear
}

const formatFinancialPeriod = (period: string) => {
    const match = period.trim().match(/FY\s*'?(\d{2,4})/i)
    if (!match) return period

    const partialQuarter = period.trim().match(/(?:Upto|Till)\s+(Q[1-4])/i)?.[1]?.toUpperCase()
    const yearLabel = `FY '${match[1].slice(-2)}`

    return partialQuarter ? `${yearLabel} (Upto ${partialQuarter})` : yearLabel
}

const numberFromUnknown = (value: unknown): number | null => {
    const parsedValue = typeof value === 'string' ? parseJsonValue(value) : value
    const numericValue = Number(parsedValue)

    return Number.isFinite(numericValue) ? numericValue : null
}

const formatFinancialSliceLabel = (label: string, value: number, totalValue: number) => {
    const percentageValue = totalValue > 0 ? (value / totalValue) * 100 : 0
    const displayValue = Number.isInteger(percentageValue) ? String(percentageValue) : percentageValue.toFixed(1)

    return `${label} (${displayValue}%)`
}

const financialMetricChange = (values: number[]) => {
    if (values.length < 2) return { text: '', tone: 'neutral' as const }

    const previousValue = values[values.length - 2]
    const currentValue = values[values.length - 1]
    if (!previousValue) return { text: '', tone: 'neutral' as const }

    const percentageChange = ((currentValue - previousValue) / Math.abs(previousValue)) * 100
    if (!Number.isFinite(percentageChange)) return { text: '', tone: 'neutral' as const }

    const sign = percentageChange > 0 ? '+' : ''
    const direction = percentageChange >= 0 ? '↑' : '↓'
    const tone = percentageChange < 0 ? 'negative' : percentageChange > 0 ? 'positive' : 'neutral'

    return {
        text: `YoY ${sign}${percentageChange.toFixed(1)}% ${direction}`,
        tone
    }
}

const financialMetricVisibility = (payload: IssuerFactPayload, metricKey: string) => {
    const fieldConfig = payload.fieldConfig
    if (!fieldConfig || typeof fieldConfig !== 'object' || Array.isArray(fieldConfig)) return true

    const metricVisibility = (fieldConfig as Record<string, unknown>).metricVisibility
    if (!metricVisibility || typeof metricVisibility !== 'object' || Array.isArray(metricVisibility)) return true

    return (metricVisibility as Record<string, unknown>)[metricKey] !== false
}

export const mapIssuerFactIssuerMetrics = (
    issuerFactsData?: BondsDetailsIssuerFactsData
): { title?: string; metrics: BondV2IssuerGlanceMetric[] } | null => {
    const module = issuerFactsData?.modules.find((item) => item.moduleKey === ISSUER_METRICS_MODULE_KEY)
    if (!module) return null

    const metrics = usableFacts(issuerFactsData, ISSUER_METRICS_MODULE_KEY)
        .sort((first, second) => {
            const order = issuerMetricFactOrder(first) - issuerMetricFactOrder(second)
            if (order !== 0) return order

            return (first.label || first.fieldKey).localeCompare(second.label || second.fieldKey)
        })
        .map((fact) => {
            const value = formattedScalarFactValue(fact)
            if (!value) return null

            return {
                icon: factSchemaIcon(fact),
                value,
                label: fact.label?.trim() || fact.fieldKey,
                tooltip: factDescription(fact) || undefined
            }
        })
        .filter(Boolean) as BondV2IssuerGlanceMetric[]

    if (!metrics.length) return null

    return {
        title: module.title,
        metrics
    }
}

export const mapIssuerFactFinancialTrend = (
    issuerFactsData?: BondsDetailsIssuerFactsData
): BondV2FinancialTrendData | null => {
    const module = issuerFactsData?.modules.find((item) => item.moduleKey === FINANCIAL_DATA_MODULE_KEY)
    if (!module) return null

    const fact = usableFacts(issuerFactsData, FINANCIAL_DATA_MODULE_KEY)
        .find((item) => item.fieldType === 'FINANCIAL_RECORD' || item.fieldKey === 'FINANCIAL_DATA')
    if (!fact) return null

    const payload = parseFactPayload(fact.valueJson)
    const metrics = parseFinancialSchemaMetrics(fact.fieldSchemaJson)
        .filter((metric) => financialMetricVisibility(payload, metric.key))
    if (!metrics.length) return null

    const rows = [...(fact.rows ?? [])]
        .map((row, index) => ({
            row,
            index,
            period: row.period == null ? '' : String(row.period).trim()
        }))
        .filter(({ period }) => period)
        .sort((first, second) => financialPeriodOrder(first.period, first.index) - financialPeriodOrder(second.period, second.index))

    const chartsPayload = payload.charts && typeof payload.charts === 'object' && !Array.isArray(payload.charts)
        ? payload.charts as Record<string, unknown>
        : {}
    const configs: Record<string, BondV2FinancialTrendConfig> = {}
    const tabs: string[] = []

    metrics.forEach((metric) => {
        const label = financialMetricLabel(metric)

        if (metric.graphType === 'DOUGHNUT' || metric.graphType === 'PIE') {
            const chartItems = Array.isArray(chartsPayload[metric.key]) ? chartsPayload[metric.key] as unknown[] : []
            const charts = chartItems
                .map<FinancialDistributionChart | null>((chartItem) => {
                    if (!chartItem || typeof chartItem !== 'object' || Array.isArray(chartItem)) return null

                    const chartRecord = chartItem as Record<string, unknown>
                    const slices = Array.isArray(chartRecord.slices) ? chartRecord.slices : []
                    const rawSlices = slices
                        .map((slice, index) => {
                            if (!slice || typeof slice !== 'object' || Array.isArray(slice)) return null

                            const sliceRecord = slice as Record<string, unknown>
                            const sliceLabel = sliceRecord.label == null ? '' : String(sliceRecord.label).trim()
                            const sliceValue = numberFromUnknown(sliceRecord.value)

                            if (!sliceLabel || sliceValue == null) return null

                            return {
                                label: sliceLabel,
                                value: sliceValue,
                                color: FINANCIAL_DISTRIBUTION_COLORS[index % FINANCIAL_DISTRIBUTION_COLORS.length]
                            }
                        })
                        .filter(Boolean) as Array<{ label: string; value: number; color: string }>
                    const totalValue = rawSlices.reduce((total, slice) => total + slice.value, 0)
                    const parsedSlices = rawSlices.map((slice) => ({
                        ...slice,
                        label: formatFinancialSliceLabel(slice.label, slice.value, totalValue)
                    }))

                    const title = chartRecord.title == null ? '' : String(chartRecord.title).trim()
                    if (!title || !parsedSlices.length) return null

                    return {
                        title,
                        tooltip: `Shows ${title}.`,
                        labels: rawSlices.map((slice) => slice.label),
                        values: parsedSlices.map((slice) => slice.value),
                        tooltipLabels: rawSlices.map((slice) => slice.label),
                        legend: parsedSlices.map((slice) => ({ label: slice.label, color: slice.color })),
                        chartType: metric.graphType === 'PIE' ? 'pie' as const : 'doughnut' as const
                    }
                })
                .filter((chart): chart is FinancialDistributionChart => chart != null)

            if (!charts.length) return

            tabs.push(label)
            configs[label] = {
                type: 'doughnut-grid',
                change: '',
                charts,
                unitType: metric.unitType
            }
            return
        }

        const seriesRows = rows
            .map(({ row, period }) => {
                const cell = row.cells.find((rowCell) => rowCell.metricKey === metric.key)
                const value = numberFromUnknown(cell?.valueJson ?? cell?.displayValue)

                return value == null
                    ? null
                    : {
                        period,
                        value
                    }
            })
            .filter(Boolean) as Array<{ period: string; value: number }>
        const values = seriesRows.map((row) => row.value)

        if (values.length < 2) return

        const change = financialMetricChange(values)

        tabs.push(label)
        configs[label] = {
            type: metric.graphType === 'BAR' ? 'bar' : 'line',
            change: change.text,
            changeTone: change.tone,
            labels: seriesRows.map(({ period }) => formatFinancialPeriod(period)),
            values,
            legend: [],
            unitType: metric.unitType
        }
    })

    if (!tabs.length) return null

    return {
        title: module.title || 'Financial trend',
        tabs,
        configs
    }
}

export const mapIssuerFactBasicDetails = (
    issuerFactsData?: BondsDetailsIssuerFactsData
): BondV2BasicDetailsData => {
    const module = issuerFactsData?.modules.find((item) => item.moduleKey === BASIC_DETAILS_MODULE_KEY)
    const hasModule = Boolean(module)
    const facts = module?.facts.filter(isUsableFact) ?? []
    const factByKey = new Map(facts.map((fact) => [fact.fieldKey, fact]))
    const aboutIssuer = factByKey.get('ABOUT_THE_ISSUER')
    const debentureTrustee = factByKey.get('DEBENTURE_TRUSTEE')
    const issuanceDate = factByKey.get('ISSUANCE_DATE')
    const modeOfIssuance = factByKey.get('MODE_OF_ISSUANCE')
    const ownership = factByKey.get('OWNERSHIP')
    const securityType = factByKey.get('SECURITY_TYPE')
    const whyThisBond = factByKey.get('WHY_THIS_BOND')
    const whyThisBondItems = listItems(whyThisBond)
    const extraRows = facts
        .filter((fact) => !BASIC_DETAILS_STANDARD_FIELD_KEYS.has(fact.fieldKey))
        .map((fact) => ({
            fieldKey: fact.fieldKey,
            label: fact.label,
            value: textValue(fact)
        }))
        .filter((row) => row.value)

    return {
        hasModule,
        title: module?.title,
        aboutIssuer: aboutIssuer && textValue(aboutIssuer)
            ? { label: aboutIssuer.label, value: textValue(aboutIssuer) }
            : undefined,
        debentureTrustee: debentureTrustee && textValue(debentureTrustee)
            ? { label: debentureTrustee.label, value: textValue(debentureTrustee) }
            : undefined,
        issuanceDate: issuanceDate && textValue(issuanceDate)
            ? { label: issuanceDate.label, value: textValue(issuanceDate) }
            : undefined,
        modeOfIssuance: modeOfIssuance && textValue(modeOfIssuance)
            ? { label: modeOfIssuance.label, value: textValue(modeOfIssuance) }
            : undefined,
        ownership: ownership && textValue(ownership)
            ? { label: ownership.label, value: textValue(ownership) }
            : undefined,
        securityType: securityType && textValue(securityType)
            ? { label: securityType.label, value: textValue(securityType) }
            : undefined,
        whyThisBond: whyThisBond && whyThisBondItems.length
            ? { label: whyThisBond.label, items: whyThisBondItems }
            : undefined,
        extraRows
    }
}

export const mapIssuerFactRegulatoryAssurance = (
    issuerFactsData?: BondsDetailsIssuerFactsData
): BondV2RegulatoryAssuranceData | null => {
    const documentsModule = issuerFactsData?.modules.find((module) => module.moduleKey === DOCUMENTS_MODULE_KEY)
    if (!documentsModule) return null

    const documents = usableFacts(issuerFactsData, DOCUMENTS_MODULE_KEY)
        .map((fact) => {
            const parsedDocument = parseDocumentValueJson(fact.valueJson)
            const document = fact.document ?? parsedDocument

            return {
                label: fact.label.trim() || fact.fieldKey,
                fileUrl: document?.fileUrl ?? '',
                fileName: document?.fileName ?? fact.displayValue ?? parsedDocument?.fileName ?? '',
                documentId: document?.documentId ?? document?.id ?? fact.documentId ?? ''
            }
        })
        .filter((document) => document.documentId && document.fileUrl)

    if (!documents.length) return null

    return {
        title: documentsModule.title,
        documents
    }
}

export const mapIssuerFactStrengthsRisks = (
    issuerFactsData?: BondsDetailsIssuerFactsData
): BondV2StrengthsRisksData | null => {
    const module = issuerFactsData?.modules.find((item) => item.moduleKey === STRENGTHS_RISK_MODULE_KEY)
    if (!module) return null

    const facts = usableFacts(issuerFactsData, STRENGTHS_RISK_MODULE_KEY)
    const factByKey = new Map(facts.map((fact) => [fact.fieldKey, fact]))
    const strengthsFact = factByKey.get('STRENGTHS')
    const risksFact = factByKey.get('RISKS')
    const strengthsItems = strengthRiskItems(strengthsFact)
    const riskItems = strengthRiskItems(risksFact)
    const strengthsTitle = strengthsFact?.label || 'Key Strengths'


    const strengths: BondV2StrengthRiskGroup | undefined = strengthsItems.length
        ? {
            title: strengthsTitle,
            items: strengthsItems
        }
        : undefined
    const risks: BondV2StrengthRiskGroup | undefined = riskItems.length
        ? {
            title: risksFact?.label || 'Key Risks',
            items: riskItems
        }
        : undefined

    if (!strengths && !risks) return null

    return {
        title: module.title || 'Strengths & Risk',
        strengths,
        risks
    }
}

export const mapIssuerFactCreditRatingIntelligence = (
    issuerFactsData: BondsDetailsIssuerFactsData | undefined,
    currentRating?: string | null
): BondV2CreditRatingIntelligenceData | null => {
    const module = issuerFactsData?.modules.find((item) => item.moduleKey === CREDIT_RATING_INTELLIGENCE_MODULE_KEY)
    const peerComparison = mapIssuerFactSectorSupportData(issuerFactsData)
    if (!module && !peerComparison) return null

    const facts = usableFacts(issuerFactsData, CREDIT_RATING_INTELLIGENCE_MODULE_KEY)
    const factByKey = new Map(facts.map((fact) => [fact.fieldKey, fact]))
    const chipsFact = factByKey.get('INTELLIGENCE_CHIPS')
    const rationaleSummaryFact = factByKey.get('RATING_RATIONALE_SUMMARY')
    const summaryPointsFact = factByKey.get('SUMMARY_POINTS')
    const timelineFact = factByKey.get('RATING_HISTORY_TIMELINE')
    const outlookFact = factByKey.get('OUTLOOK_IMPLICATIONS')
    const implicationsFact = factByKey.get('IMPLICATIONS_FOR_INVESTORS')
    const watchForFact = factByKey.get('WATCH_FOR')
    const ratingDriversFact = factByKey.get('RATING_DRIVERS')
    const chips = listItems(chipsFact).slice(0, 4)
    const summaryPoints = listItems(summaryPointsFact).slice(0, 4)
    const timelineItems = ratingTimelineItems(timelineFact)
    const implications = listItems(implicationsFact).slice(0, 5)
    const ratingDrivers = ratingDriverItems(ratingDriversFact)
    const outlookValue = textValue(outlookFact)
    const rationaleSummaryValue = textValue(rationaleSummaryFact)
    const watchFor = textValue(watchForFact)
    const latestTimelineItem = timelineItems[timelineItems.length - 1]
    const displayRating = latestTimelineItem?.rating || (module ? currentRating : '')
    const currentRatingLabel = displayRating
        ? `${displayRating}${chips.some((chip) => chip.toLowerCase().includes('stable')) ? '/ Stable' : ''}`
        : ''

    if (
        !currentRatingLabel
        && !chips.length
        && !rationaleSummaryValue
        && !summaryPoints.length
        && !timelineItems.length
        && !outlookValue
        && !implications.length
        && !watchFor
        && !ratingDrivers.length
        && !peerComparison
    ) {
        return null
    }

    return {
        title: module?.title || 'Credit Rating Intelligence',
        description: module?.description,
        currentRating: currentRatingLabel || undefined,
        currentRatingStatus: latestTimelineItem?.title,
        chips,
        rationaleSummary: rationaleSummaryValue
            ? {
                label: rationaleSummaryFact?.label || 'Rating rationale summary',
                value: rationaleSummaryValue
            }
            : undefined,
        summaryPoints: summaryPoints.length
            ? {
                label: summaryPointsFact?.label,
                items: summaryPoints
            }
            : undefined,
        timeline: timelineItems.length
            ? {
                label: timelineFact?.label,
                items: timelineItems
            }
            : undefined,
        outlook: outlookValue || implications.length || watchFor
            ? {
                label: outlookFact?.label,
                value: outlookValue,
                implicationsLabel: implicationsFact?.label,
                implications,
                watchForLabel: watchForFact?.label,
                watchFor
            }
            : undefined,
        drivers: ratingDrivers.length
            ? {
                label: ratingDriversFact?.label,
                items: ratingDrivers
            }
            : undefined,
        peerComparison
    }
}
